/**
 * Regular expression string for matching YouTube video IDs from various URL formats.
 *
 * Matches YouTube URLs including standard watch URLs, shortened URLs, embed URLs,
 * and YouTube Shorts URLs. The video ID is captured in the second match group.
 *
 * @example
 * ```typescript
 * const regex = new RegExp(youTubeIdRegExpString)
 * const match = 'https://www.youtube.com/watch?v=dQw4w9WgXcQ'.match(regex)
 * const videoId = match[2] // 'dQw4w9WgXcQ'
 * ```
 *
 * @see https://github.com/EduBeyond/youtube-url-to-embed
 */
export const youTubeIdRegExpString = `^.*(youtu.be\\/|v\\/|u\\/\\w\\/|embed\\/|shorts\\/|watch\\?v=|&v=)([^#&?]*).*`

/**
 * Regular expression string for matching Vimeo video IDs from various URL formats.
 *
 * Matches Vimeo URLs including standard watch URLs, player URLs, and embed URLs.
 * The video ID is captured in the first match group.
 *
 * @example
 * ```typescript
 * const regex = new RegExp(vimeoIdRegExpString)
 * const match = 'https://vimeo.com/123456789'.match(regex)
 * const videoId = match[1] // '123456789'
 * ```
 *
 * @see https://regexr.com/4lrm3
 */
export const vimeoIdRegExpString = `(?:http:|https:|)\\/\\/(?:player.|www.)?vimeo\\.com\\/(?:video\\/|embed\\/|watch\\?\\S*v=|v\\/)?(\\d*)`

/**
 * Extracts the YouTube video ID from a YouTube URL.
 *
 * Handles various YouTube URL formats including standard watch URLs,
 * shortened URLs, embed URLs, and YouTube Shorts URLs.
 *
 * @param url - The YouTube URL to extract the ID from
 * @returns The YouTube video ID if valid, null otherwise
 */
export const getYouTubeId = (url: string): null | string => {
  const regExp = new RegExp(youTubeIdRegExpString)
  const match = url.match(regExp)

  return match && match[2] && match[2].length === 11 ? match[2] : null
}

/**
 * Extracts the Vimeo video ID from a Vimeo URL.
 *
 * Handles various Vimeo URL formats including standard watch URLs,
 * player URLs, and embed URLs.
 *
 * @param url - The Vimeo URL to extract the ID from
 * @returns The Vimeo video ID if valid, null otherwise
 */
export const getVimeoId = (url: string): string | null | undefined => {
  const regExp = new RegExp(vimeoIdRegExpString)
  const match = url.match(regExp)

  return match ? match[1] : null
}

/**
 * Converts a YouTube or Vimeo URL to its corresponding embed URL.
 *
 * Detects whether the URL is from YouTube or Vimeo and returns the
 * appropriate embed URL format. If the URL is neither, returns the original URL.
 *
 * @param url - The video URL to convert to an embed URL
 * @returns The embed URL for the video, or the original URL if not recognized
 */
const getVideoEmbedURL = (url: string): string => {
  const youTubeId = getYouTubeId(url)
  const vimeoId = getVimeoId(url)

  if (youTubeId) {
    return `https://www.youtube.com/embed/${youTubeId}`
  }
  if (vimeoId) {
    return `https://player.vimeo.com/video/${vimeoId}`
  }

  return url
}

export default getVideoEmbedURL
